import { getSectionHTML } from '@theme/utilities';

class LoadMore extends HTMLElement {
  #storageKey = `load-more:${window.location.pathname}`;

  get button() {
    return this.querySelector('button');
  }

  get progressBar() {
    return this.querySelector('[data-progress-bar]');
  }

  get countText() {
    return this.querySelector('[data-count]');
  }

  get grid() {
    return this.closest('.shopify-section').querySelector('[data-product-grid]');
  }

  get currentPage() {
    return parseInt(this.dataset.currentPage, 10);
  }

  set currentPage(value) {
    this.dataset.currentPage = value;
  }

  get totalPages() {
    return parseInt(this.dataset.totalPages, 10);
  }

  get totalItems() {
    return parseInt(this.dataset.totalItems, 10);
  }

  get pageSize() {
    return parseInt(this.dataset.pageSize, 10);
  }

  get sectionId() {
    return this.closest('.shopify-section')?.id;
  }

  connectedCallback() {
    console.log('[load-more] connected', {
      sectionId: this.sectionId,
      grid: this.grid,
      button: this.button,
      currentPage: this.currentPage,
      totalPages: this.totalPages,
      totalItems: this.totalItems,
      pageSize: this.pageSize,
    });
    this.button.addEventListener('click', this.#handleClick);
    window.addEventListener('pageshow', this.#handlePageShow);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);
  }

  disconnectedCallback() {
    this.button.removeEventListener('click', this.#handleClick);
    window.removeEventListener('pageshow', this.#handlePageShow);
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
  }

  #getLastVisibleCardIndex() {
    const cards = this.grid.children;
    const viewportBottom = window.scrollY + window.innerHeight;
    let lastIndex = 0;
    for (let i = 0; i < cards.length; i++) {
      if (cards[i].offsetTop <= viewportBottom) lastIndex = i;
      else break;
    }
    return lastIndex;
  }

  #saveState() {
    if (this.currentPage <= 1) return;
    try {
      sessionStorage.setItem(
        this.#storageKey,
        JSON.stringify({ currentPage: this.currentPage, cardIndex: this.#getLastVisibleCardIndex() })
      );
    } catch {
      // sessionStorage unavailable — fail silently
    }
  }

  #handleVisibilityChange = () => {
    if (document.visibilityState === 'hidden') {
      this.#saveState();
    }
  };

  #handlePageShow = (event) => {
    if (event.persisted) {
      sessionStorage.removeItem(this.#storageKey);
      return;
    }
    this.#maybeRestore();
  };

  async #maybeRestore() {
    let state;
    try {
      const raw = sessionStorage.getItem(this.#storageKey);
      if (!raw) return;
      state = JSON.parse(raw);
    } catch {
      return;
    }

    console.log('[load-more] restore state', state);
    if (!state || state.currentPage <= 1) return;

    sessionStorage.removeItem(this.#storageKey);
    this.button.disabled = true;

    const pageNumbers = [];
    for (let p = 2; p <= state.currentPage; p++) pageNumbers.push(p);

    try {
      const htmlResults = await Promise.all(
        pageNumbers.map((pageNum) => {
          const url = new URL(window.location.href);
          url.searchParams.set('page', pageNum);
          return getSectionHTML(this.sectionId, url);
        })
      );

      const parser = new DOMParser();
      for (const html of htmlResults) {
        parser.parseFromString(html, 'text/html')
          .querySelectorAll('[data-product-grid] > *')
          .forEach((el) => this.grid.appendChild(el));
      }

      this.currentPage = state.currentPage;
      this.#updateProgress();

      if (this.currentPage >= this.totalPages) {
        this.remove();
        return;
      }
    } catch (error) {
      console.error(error);
    } finally {
      if (this.button) this.button.disabled = false;
    }

    const card = this.grid.children[state.cardIndex];
    if (card) {
      card.scrollIntoView({ behavior: 'instant', block: 'center' });
    }
  }

  #handleClick = async () => {
    const nextPage = this.currentPage + 1;
    console.log('[load-more] click', { currentPage: this.currentPage, nextPage, totalPages: this.totalPages });
    if (nextPage > this.totalPages) {
      console.log('[load-more] nextPage > totalPages, aborting');
      return;
    }

    this.button.disabled = true;

    try {
      const url = new URL(window.location.href);
      url.searchParams.set('page', nextPage);
      console.log('[load-more] fetching', { sectionId: this.sectionId, url: url.toString() });

      const html = await getSectionHTML(this.sectionId, url);
      console.log('[load-more] response', { htmlLength: html?.length ?? null, isNull: html === null });
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const cards = parsed.querySelectorAll('[data-product-grid] > *');
      console.log('[load-more] parsed', {
        cardsFound: cards.length,
        hasGridInResponse: !!parsed.querySelector('[data-product-grid]'),
        localGrid: this.grid,
      });
      cards.forEach((el) => this.grid.appendChild(el));

      this.currentPage = nextPage;
      this.#updateProgress();
      this.#saveState();

      if (nextPage >= this.totalPages) {
        this.remove();
      }
    } catch (error) {
      console.error('[load-more] click failed', error);
    } finally {
      this.button.disabled = false;
    }
  };

  #updateProgress() {
    const viewed = Math.min(this.currentPage * this.pageSize, this.totalItems);

    if (this.progressBar) {
      this.progressBar.style.width = `${(viewed / this.totalItems) * 100}%`;
    }

    if (this.countText) {
      this.countText.textContent = `You've viewed ${viewed} out of ${this.totalItems} products`;
    }
  }
}

if (!customElements.get('load-more')) {
  customElements.define('load-more', LoadMore);
}
